import type { MoneyOrder, SellerFee } from '@guildite/domain/payouts';

import { orderFromRecord } from './adapters/order.ts';
import type { PayoutAccountView, PayoutsPort, ResolvedPayout, ShopPayoutState } from './ports.ts';
import type { WorkerClient } from './worker.ts';

type Rows = { id: string; data: Record<string, unknown> }[];
type Query = (collection: string, options: { filters?: [string, '==', unknown][]; orderBy?: [string, 'asc' | 'desc']; limit?: number }) => Promise<Rows>;

export function createPayouts({ worker, query, currentUid }: { worker: WorkerClient; query: Query; currentUid: () => string | null }): PayoutsPort {
  return {
    account: () => worker.call<PayoutAccountView | null>('get_payout_account'),
    banks: () => worker.call<{ code: string; name: string }[]>('list_settlement_banks'),
    resolve: destination => worker.call<ResolvedPayout>('resolve_account', destination),
    save: async (destination, verificationToken) => { await worker.call('save_payout_account', { ...destination, verificationToken }); },
    shopAccount: shopId => worker.call<ShopPayoutState>('get_shop_payout_account', { shopId }),
    saveForShop: async (shopId, destination, verificationToken) => { await worker.call('save_shop_payout_account', { shopId, ...destination, verificationToken }); },
    async money() {
      const uid = currentUid();
      if (!uid) return { orders: [], fees: [] };
      const [orderRows, feeRows] = await Promise.all([
        query('orders', { filters: [['sellerId', '==', uid]], orderBy: ['createdAt', 'desc'], limit: 300 }),
        query('sellerFees', { filters: [['sellerId', '==', uid]], limit: 300 }),
      ]);
      const orders = orderRows.map(({ id, data }): MoneyOrder => {
        const view = orderFromRecord(id, data);
        return {
          id, status: view.status, shopName: view.shopName, paidAt: view.paidAt,
          sellerItemsMinor: view.amounts.sellerItemsMinor, deliveryMinor: view.amounts.deliveryMinor, totalMinor: view.amounts.totalMinor,
        };
      });
      const fees = feeRows.map(({ id, data }): SellerFee => ({
        orderId: String(data.orderId || id), amountMinor: Math.round(Number(data.amountMinor) || 0), reason: String(data.reason || ''),
        status: String(data.status || ''), createdAt: String(data.createdAt || ''),
      }));
      return { orders, fees };
    },
  };
}
